import { useState, useEffect, useCallback, useRef } from 'react';

// Απλό global cache store στη μνήμη
interface CacheItem<T> {
    data: T;
    timestamp: number;
}

const apiCache = new Map<string, CacheItem<any>>();

const DEFAULT_CACHE_TTL = 10 * 60 * 1000;

export function useApi<T>(
    key: string, // Ένα μοναδικό κλειδί για την cache (π.χ. "users", "surgery-types")
    serviceMethod: (signal?: AbortSignal) => Promise<T>,
    dependencies: any[] = [],
    cacheTtl: number = DEFAULT_CACHE_TTL
) {
    // Αρχικοποίηση από την cache αν υπάρχει και είναι έγκυρη
    const getCachedData = (): T | null => {
        const cached = apiCache.get(key);
        if (cached && Date.now() - cached.timestamp < cacheTtl) {
            return cached.data;
        }
        return null;
    };

    const cachedInitial = getCachedData();

    const [data, setData] = useState<T | null>(cachedInitial);
    const [loading, setLoading] = useState<boolean>(cachedInitial === null);
    const [error, setError] = useState<string | null>(null);

    // Αποθήκευση του service method σε ref για να αποφύγουμε περιττά re-renders
    const serviceMethodRef = useRef(serviceMethod);
    serviceMethodRef.current = serviceMethod;

    const fetchData = useCallback(async (signal?: AbortSignal, forceRefresh: boolean = false) => {
        // Αν δεν ζητήθηκε αναγκαστική ανανέωση, ελέγχουμε την cache
        if (!forceRefresh) {
            const validCached = getCachedData();
            if (validCached !== null) {
                setData(validCached);
                setLoading(false);
                return;
            }
        }

        setLoading(true);
        setError(null);

        try {
            const result = await serviceMethodRef.current(signal);
            
            // Αποθήκευση στη μνήμη (cache)
            apiCache.set(key, { data: result, timestamp: Date.now() });
            
            setData(result);
        } catch (err: unknown) {
            const errorObj = err as any;
            if (errorObj.name !== 'CanceledError' && errorObj.code !== 'ERR_CANCELED') {
                setError(errorObj.message || 'Άγνωστο σφάλμα');
            }
        } finally {
            setLoading(false);
        }
    }, [key, cacheTtl]);

    useEffect(() => {
        const controller = new AbortController();
        
        // Αν δεν έχουμε ήδη valid cache, τρέχουμε το fetch
        if (!getCachedData()) {
            fetchData(controller.signal);
        } else {
            setLoading(false);
        }
        
        return () => {
            controller.abort();
        };
    }, [fetchData]);

    // Συνάρτηση για καθαρισμό της cache συγκεκριμένου κλειδιού (π.χ. μετά από create/delete)
    const invalidateCache = () => {
        apiCache.delete(key);
    };

    return {
        data,
        loading,
        error,
        refetch: (force: boolean = true) => fetchData(undefined, force),
        invalidateCache,
    };
}